import { useState } from 'react'
import { download, request } from '../lib/api'
import { useApi } from '../lib/useApi'
import type { ManagedDatabase } from '../lib/types'

type Migration = {
  id: string; status: string; uploaded_bytes: number; error: string | null
  report: { row_counts_match: boolean; columns_match: boolean; extensions_match: boolean } | null
}
const stages: Record<string, string> = {
  uploading: 'Upload your snapshot', queued: 'Import queued', preflight: 'Checking the target',
  restoring: 'Restoring your database', verifying: 'Comparing the restored snapshot',
  verified: 'Snapshot verified', failed: 'Migration needs attention',
}

export default function DatabaseMigration({ database }: { database: ManagedDatabase }) {
  const path = `/v1/databases/${database.id}/migrations`
  const { data, error: loadError, reload } = useApi(() => request<Migration[]>(path), [path], 5000)
  const [archive, setArchive] = useState<File | null>(null)
  const [manifest, setManifest] = useState<File | null>(null)
  const [confirmation, setConfirmation] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState<number | null>(null)
  const current = data?.[0]
  const ready = database.status === 'available' && database.linked_services.length === 0

  async function upload() {
    if (!archive || (!current && !manifest)) return
    setBusy(true); setError('')
    try {
      if (archive.size > 2 * 1024 ** 3) throw new Error('Maximum archive size is 2 GiB. Contact support for a larger migration.')
      if (manifest && manifest.size > 1024 ** 2) throw new Error('Manifest exceeds 1 MiB.')
      let row = current ?? await request<Migration>(path, {
        method: 'POST', body: await manifest!.text(),
      })
      if (row.uploaded_bytes > archive.size) throw new Error('Select the original archive to resume this upload.')
      for (let offset = row.uploaded_bytes; offset < archive.size;) {
        const chunk = archive.slice(offset, offset + 8 * 1024 ** 2)
        row = await request<Migration>(`${path}/${row.id}/archive?offset=${offset}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/octet-stream' }, body: chunk,
        })
        offset = row.uploaded_bytes; setSent(offset)
      }
      reload()
    } catch (e) { setError(e instanceof Error ? e.message : 'Upload failed. Select the same file to resume.'); reload() }
    finally { setBusy(false) }
  }

  async function start() {
    if (!current) return
    setBusy(true); setError('')
    try {
      await request(`${path}/${current.id}/start`, {
        method: 'POST', body: JSON.stringify({ confirm_target: confirmation }),
      })
      reload()
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not start the import.') }
    finally { setBusy(false) }
  }

  return <section className="panel" id="migration" aria-labelledby="migration-title">
    <div className="panel-head"><h2 id="migration-title">Migrate to TalariCloud</h2></div>
    <div className="panel-body stack">
      <p>Bring a PostgreSQL {database.version} database from Render or another provider. Your source stays live while you rehearse the migration here.</p>
      {!current && <>
        <h3>Prepare a consistent snapshot</h3>
        <p>Download the snapshot exporter below. It creates a backup and verification manifest from the same read-only source snapshot.</p>
        <button className="btn ghost" disabled={busy} onClick={async () => {
          setBusy(true); setError('')
          try { await download(`/v1/databases/${database.id}/migration-exporter`, 'talaricloud-postgres-export.zip') }
          catch (e) { setError(e instanceof Error ? e.message : 'Download failed. Please retry.') }
          finally { setBusy(false) }
        }}>Download snapshot exporter</button>
        <p>Extract the ZIP, then follow its README to install the client tools and Python environment.</p>
        <pre><code>.venv/bin/python export-postgres.py --output ./migration-export</code></pre>
        <p className="muted">Requires Python with asyncpg and PostgreSQL {database.version} client tools. Source credentials stay on your machine. Keep exported files private.</p>
      </>}
      {(loadError || error || current?.error) && <div className="alert error" role="alert">{error || current?.error || loadError}</div>}
      {!ready && !current && <div className="alert">Use an available, empty database without linked applications. Resolve any storage provisioning error first.</div>}
      {current && <p role="status" aria-live="polite"><strong>{stages[current.status] ?? current.status}</strong></p>}
      {(!current || current.status === 'uploading') && <>
        {!current && <label>Verification manifest (.json)<input type="file" accept=".json,application/json" disabled={busy} onChange={e => setManifest(e.target.files?.[0] ?? null)} /></label>}
        <label>Database archive (.dump)<input type="file" accept=".dump" disabled={busy} onChange={e => setArchive(e.target.files?.[0] ?? null)} /></label>
        <p className="muted">Up to 2 GiB. Interrupted uploads resume when you select the original file. Archives are retained for seven days.</p>
        {(sent !== null || current) && <p role="status">{((sent ?? current?.uploaded_bytes ?? 0) / 1024 ** 2).toFixed(1)} MiB uploaded</p>}
        <button className="btn" disabled={busy || !ready || !archive || (!current && !manifest) || !!loadError || !data} onClick={upload}>{busy ? 'Working…' : current ? 'Resume upload' : 'Upload snapshot'}</button>
        {current && current.uploaded_bytes > 0 && <>
          <label>Type <strong>{database.name}</strong> to confirm the import<input value={confirmation} disabled={busy} onChange={e => setConfirmation(e.target.value)} autoComplete="off" /></label>
          <button className="btn primary" disabled={busy || !ready || confirmation !== database.name} onClick={start}>Check archive & start import</button>
        </>}
      </>}
      {current && ['uploading', 'queued'].includes(current.status) && <button className="btn ghost" disabled={busy} onClick={async () => {
        setBusy(true); setError('')
        try { await request(`${path}/${current.id}/cancel`, { method: 'POST' }); reload() }
        catch (e) { setError(e instanceof Error ? e.message : 'Could not cancel migration.') }
        finally { setBusy(false) }
      }}>Cancel migration</button>}
      {current?.report && <>
        <dl>
          <dt>Exact table row counts</dt><dd>{current.report.row_counts_match ? 'Match' : 'Mismatch'}</dd>
          <dt>Columns and data types</dt><dd>{current.report.columns_match ? 'Match' : 'Mismatch'}</dd>
          <dt>Extension versions</dt><dd>{current.report.extensions_match ? 'Match' : 'Mismatch'}</dd>
        </dl>
        <button className="btn ghost" onClick={() => {
          const url = URL.createObjectURL(new Blob([JSON.stringify(current.report, null, 2)], { type: 'application/json' }))
          const link = document.createElement('a'); link.href = url; link.download = 'migration-verification.json'; link.click(); URL.revokeObjectURL(url)
        }}>Download verification report</button>
      </>}
      {current?.status === 'verified' && <>
        <h3>Before you cut over</h3>
        <p>This verifies the exported snapshot, not later source writes or full application behavior.</p>
        <ol><li>Test your application against this private target.</li><li>Schedule a source write pause and export a final snapshot into a fresh target.</li><li>Verify again, then explicitly update your application connection and routing.</li><li>Keep the source available for your agreed rollback window.</li></ol>
      </>}
      <p className="muted">TalariCloud does not change your source database, application connections or DNS during import. This is a snapshot migration, not continuous replication.</p>
    </div>
  </section>
}
